import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Search, Filter, Eye, MoreHorizontal, Phone, Mail, MapPin, Plus, Trash2, FileText } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { createProjectFromQuote, deleteQuote, getQuotes, updateQuote } from '../../services/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useUnread } from '../../context/UnreadContext';
import { UnreadBadge, UnreadDot } from '../../components/dashboard/UnreadBadge';
import { CustomerRequestsPanel } from '../../components/dashboard/CustomerRequestsPanel';
import { NewQuoteDialog, QUOTE_TYPES } from '../../components/dashboard/NewQuoteDialog';
import { EstimateDialog } from '../../components/dashboard/EstimateDialog';
import { formatAmount } from '../../lib/estimate';

const REQUEST_TABS = [
  { value: 'quotes', label: 'Soumissions' },
  { value: 'cleaning_requests', label: 'Nettoyages' },
  { value: 'part_orders', label: 'Pièces' }
];

export const QuotesPage = () => {
  const { can } = useAuth();
  const { counts, isUnread, markRead } = useUnread();
  const [searchParams, setSearchParams] = useSearchParams();
  // Onglets visibles selon les permissions (soumissions, nettoyages, pièces).
  const tabs = REQUEST_TABS.filter(tab => can(`${tab.value}.view`));
  const requestedTab = searchParams.get('onglet');
  const activeTab = tabs.some(tab => tab.value === requestedTab) ? requestedTab : (tabs[0]?.value || 'quotes');
  const setActiveTab = (value) => setSearchParams(value === tabs[0]?.value ? {} : { onglet: value }, { replace: true });
  const canViewQuotes = can('quotes.view');
  const canEditQuotes = can('quotes.edit');
  const canConvert = can('quotes.convert') && can('projects.create');
  const canDeleteQuotes = can('quotes.delete');
  const canCreateQuotes = can('quotes.create');
  const [newQuoteOpen, setNewQuoteOpen] = useState(false);
  const [estimateQuote, setEstimateQuote] = useState(null);
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [projectQuote, setProjectQuote] = useState(null);
  const [projectOpen, setProjectOpen] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [projectForm, setProjectForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    address: '',
    description: '',
    work_type: 'installation',
    material_details: '',
    start_date: ''
  });

  useEffect(() => {
    if (canViewQuotes) {
      fetchQuotes();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewQuotes]);

  const fetchQuotes = async () => {
    try {
      const response = await getQuotes();
      setQuotes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Erreur lors du chargement des soumissions');
    } finally {
      setLoading(false);
    }
  };

  const openEstimate = (quote) => {
    setEstimateQuote(quote);
    setEstimateOpen(true);
    markRead('quotes', quote.id);
  };

  // Met à jour la soumission dans la liste après un devis enregistré ou envoyé.
  const handleQuoteChanged = (updated) => {
    setQuotes(current => current.map(quote => (quote.id === updated.id ? { ...quote, ...updated } : quote)));
    setEstimateQuote(updated);
    if (selectedQuote?.id === updated.id) setSelectedQuote({ ...selectedQuote, ...updated });
  };

  const handleStatusUpdate = async (quoteId, newStatus) => {
    try {
      await updateQuote(quoteId, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedQuote) return;
    try {
      await updateQuote(selectedQuote.id, { notes });
      toast.success('Notes enregistrées');
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteQuote = async () => {
    if (!quoteToDelete || deleting) return;

    setDeleting(true);
    try {
      await deleteQuote(quoteToDelete.id);
      setQuotes(currentQuotes => currentQuotes.filter(quote => quote.id !== quoteToDelete.id));

      if (selectedQuote?.id === quoteToDelete.id) {
        setDetailsOpen(false);
        setSelectedQuote(null);
      }

      toast.success('Demande de soumission supprimée');
      setDeleteOpen(false);
      setQuoteToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const openProjectForm = (quote) => {
    const repairTypes = ['reparation', 'electromenager'];
    setProjectQuote(quote);
    setProjectForm({
      client_name: [quote.first_name, quote.last_name].filter(Boolean).join(' '),
      client_email: quote.email || '',
      client_phone: quote.phone || '',
      address: quote.address || '',
      description: quote.description || '',
      work_type: repairTypes.includes(quote.installation_type) ? 'repair' : 'installation',
      material_details: '',
      start_date: quote.preferred_date || ''
    });
    setProjectOpen(true);
  };

  const handleCreateProject = async (event) => {
    event.preventDefault();
    if (!projectQuote || creatingProject) return;

    setCreatingProject(true);
    try {
      await createProjectFromQuote(projectQuote.id, projectForm);
      setQuotes(currentQuotes => currentQuotes.filter(quote => quote.id !== projectQuote.id));
      setProjectOpen(false);
      setProjectQuote(null);
      toast.success('Chantier créé; la soumission a été archivée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du chantier');
    } finally {
      setCreatingProject(false);
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.address?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-pending',
      contacted: 'badge-confirmed',
      quoted: 'badge-in-progress',
      accepted: 'badge-completed',
      rejected: 'badge-cancelled'
    };
    return badges[status] || 'badge-pending';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      contacted: 'Contacté',
      quoted: 'Devis envoyé',
      accepted: 'Accepté',
      rejected: 'Refusé'
    };
    return labels[status] || status;
  };

  const getTypeLabel = (type) => {
    const formType = QUOTE_TYPES.find(item => item.value === type);
    if (formType) return formType.label;
    const labels = {
      climatisation: 'Climatisation',
      chauffage: 'Chauffage',
      ventilation: 'Ventilation',
      electromenager: 'Électroménager',
      autre: 'Autre'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">Demandes de soumission</h1>
          <p className="text-slate-500">
            {tabs.length > 1 ? 'Gérez les demandes de soumission, de nettoyage et de pièces' : 'Gérez les demandes de devis clients'}
          </p>
        </div>
        {canCreateQuotes && (
          <Button
            onClick={() => setNewQuoteOpen(true)}
            className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8]"
            data-testid="new-quote-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle soumission
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
      {tabs.length > 1 && (
        <TabsList className="grid h-auto w-full grid-cols-3 gap-1 rounded-xl bg-white p-1 shadow-sm border border-slate-200 sm:inline-flex sm:w-auto">
          {tabs.map(tab => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="gap-1.5 rounded-lg px-2 py-2 text-slate-600 sm:gap-2 sm:px-4 data-[state=active]:bg-[#00b4d8] data-[state=active]:text-white data-[state=active]:shadow-none"
              data-testid={`tab-${tab.value}`}
            >
              {tab.label}
              <UnreadBadge count={counts[tab.value]} />
            </TabsTrigger>
          ))}
        </TabsList>
      )}

      {canViewQuotes && (
      <TabsContent value="quotes" className="space-y-6 mt-6">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher par nom ou adresse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="contacted">Contacté</SelectItem>
                <SelectItem value="quoted">Devis envoyé</SelectItem>
                <SelectItem value="accepted">Accepté</SelectItem>
                <SelectItem value="rejected">Refusé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {filteredQuotes.length > 0 ? (
            <div className="divide-y">
              {filteredQuotes.map((quote) => (
                <div 
                  key={quote.id}
                  className="p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`quote-row-${quote.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="relative w-12 h-12 shrink-0 rounded-full bg-[#00b4d8] flex items-center justify-center text-white font-semibold">
                          {quote.first_name?.[0]}{quote.last_name?.[0]}
                          <UnreadDot show={isUnread('quotes', quote.id)} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#1a3a5c]">
                            {quote.first_name} {quote.last_name}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {getTypeLabel(quote.installation_type)}
                            {quote.source === 'portal' && <span className="ml-2 text-xs text-slate-400">· saisie par {quote.created_by_name}</span>}
                          </p>
                          {quote.estimate && (
                            <p className="text-sm text-[#1a3a5c] flex items-center gap-1 mt-1">
                              <FileText className="w-4 h-4 text-[#00b4d8]" />
                              Devis {quote.estimate.number} · {formatAmount(quote.estimate.total)}
                              {quote.estimate.status === 'sent' && <span className="text-xs text-green-700">· envoyé</span>}
                            </p>
                          )}
                          <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                            <MapPin className="w-4 h-4" />
                            {quote.address || '-'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <div className="text-sm">
                        <p className="text-slate-500">
                          {new Date(quote.created_at).toLocaleDateString('fr-CA')}
                        </p>
                      </div>

                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(quote.status)}`}>
                        {getStatusLabel(quote.status)}
                      </span>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedQuote(quote);
                            setNotes(quote.notes || '');
                            setDetailsOpen(true);
                            markRead('quotes', quote.id);
                          }}
                          data-testid={`view-details-${quote.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>

                        {(canEditQuotes || quote.estimate) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEstimate(quote)}
                            title="Devis chiffré"
                            data-testid={`estimate-${quote.id}`}
                          >
                            <FileText className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">Devis</span>
                          </Button>
                        )}

                        {(canEditQuotes || canConvert || canDeleteQuotes) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {canEditQuotes && (<>
                              <DropdownMenuItem onClick={() => handleStatusUpdate(quote.id, 'contacted')}>
                                Marquer contacté
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusUpdate(quote.id, 'quoted')}>
                                Devis envoyé
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusUpdate(quote.id, 'accepted')}>
                                Accepté
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleStatusUpdate(quote.id, 'rejected')}
                                className="text-red-600"
                              >
                                Refusé
                              </DropdownMenuItem>
                              </>)}
                              {canConvert && quote.status === 'accepted' && (
                                <>
                                  <div className="my-1 h-px bg-slate-200" />
                                  <DropdownMenuItem
                                    onClick={() => openProjectForm(quote)}
                                    className="text-[#087f9c] focus:text-[#087f9c]"
                                    data-testid={`create-project-from-quote-${quote.id}`}
                                  >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Créer le chantier
                                  </DropdownMenuItem>
                                </>
                              )}
                              {canDeleteQuotes && (<>
                              <div className="my-1 h-px bg-slate-200" />
                              <DropdownMenuItem
                                onClick={() => {
                                  setQuoteToDelete(quote);
                                  setDeleteOpen(true);
                                }}
                                className="text-red-700 focus:bg-red-50 focus:text-red-700"
                                data-testid={`delete-quote-${quote.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer définitivement
                              </DropdownMenuItem>
                              </>)}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <p>Aucune demande de soumission trouvée</p>
            </div>
          )}
        </CardContent>
      </Card>
      </TabsContent>
      )}

      {tabs.filter(tab => tab.value !== 'quotes').map(tab => (
        <TabsContent key={tab.value} value={tab.value} className="mt-6">
          <CustomerRequestsPanel kind={tab.value} />
        </TabsContent>
      ))}
      </Tabs>

      {/* Create project from accepted quote */}
      <Dialog
        open={projectOpen}
        onOpenChange={(open) => {
          if (creatingProject) return;
          setProjectOpen(open);
          if (!open) setProjectQuote(null);
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer le chantier</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Les informations de la soumission sont déjà remplies. Vérifiez-les avant de créer le chantier.
          </p>
          <form onSubmit={handleCreateProject} className="space-y-4">
            <div>
              <Label>Nom du client *</Label>
              <Input
                value={projectForm.client_name}
                onChange={(event) => setProjectForm({ ...projectForm, client_name: event.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Courriel</Label>
                <Input
                  type="email"
                  value={projectForm.client_email}
                  onChange={(event) => setProjectForm({ ...projectForm, client_email: event.target.value })}
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={projectForm.client_phone}
                  onChange={(event) => setProjectForm({ ...projectForm, client_phone: event.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Adresse *</Label>
              <Input
                value={projectForm.address}
                onChange={(event) => setProjectForm({ ...projectForm, address: event.target.value })}
                required
              />
            </div>
            <div>
              <Label>Type de travaux</Label>
              <Select
                value={projectForm.work_type}
                onValueChange={(value) => setProjectForm({ ...projectForm, work_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="installation">Installation</SelectItem>
                  <SelectItem value="repair">Réparation</SelectItem>
                  <SelectItem value="maintenance">Entretien</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={projectForm.description}
                onChange={(event) => setProjectForm({ ...projectForm, description: event.target.value })}
                required
              />
            </div>
            <div>
              <Label>Détails du matériel</Label>
              <Textarea
                value={projectForm.material_details}
                onChange={(event) => setProjectForm({ ...projectForm, material_details: event.target.value })}
                placeholder="Couleur des caches, longueur de tuyaux, PEX ou cuivre..."
              />
            </div>
            <div>
              <Label>Date de début</Label>
              <Input
                type="date"
                value={projectForm.start_date}
                onChange={(event) => setProjectForm({ ...projectForm, start_date: event.target.value })}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setProjectOpen(false)}
                disabled={creatingProject}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-[#00b4d8] hover:bg-[#009bb8]"
                disabled={creatingProject}
                data-testid="confirm-create-project"
              >
                {creatingProject ? 'Création...' : 'Créer le chantier'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (deleting) return;
          setDeleteOpen(open);
          if (!open) setQuoteToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette demande?</AlertDialogTitle>
            <AlertDialogDescription>
              La demande de {quoteToDelete?.first_name} {quoteToDelete?.last_name} sera supprimée
              définitivement. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                handleDeleteQuote();
              }}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-quote"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NewQuoteDialog
        open={newQuoteOpen}
        onOpenChange={setNewQuoteOpen}
        onCreated={(quote) => setQuotes(current => [quote, ...current])}
      />

      <EstimateDialog
        quote={estimateQuote}
        open={estimateOpen}
        onOpenChange={setEstimateOpen}
        onChanged={handleQuoteChanged}
        canEdit={canEditQuotes}
      />

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détails de la soumission</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-medium">{selectedQuote.first_name} {selectedQuote.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Téléphone</p>
                  <p className="font-medium">{selectedQuote.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Courriel</p>
                  <p className="font-medium">{selectedQuote.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Type</p>
                  <p className="font-medium">{getTypeLabel(selectedQuote.installation_type)}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-slate-500">Adresse</p>
                <p className="font-medium">{selectedQuote.address}</p>
              </div>

              <div>
                <p className="text-sm text-slate-500">Description</p>
                <p className="font-medium bg-slate-50 p-3 rounded-lg">{selectedQuote.description}</p>
              </div>

              {selectedQuote.availability_text && (
                <div>
                  <p className="text-sm text-slate-500">Disponibilités</p>
                  <p className="font-medium">{selectedQuote.availability_text}</p>
                </div>
              )}

              {(canEditQuotes || selectedQuote.estimate) && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
                  <p className="text-sm">
                    {selectedQuote.estimate
                      ? <>Devis <strong>{selectedQuote.estimate.number}</strong> · {formatAmount(selectedQuote.estimate.total)}{selectedQuote.estimate.status === 'sent' ? ' · envoyé' : ' · brouillon'}</>
                      : <span className="text-slate-500">Aucun devis chiffré</span>}
                  </p>
                  <Button size="sm" variant="outline" onClick={() => openEstimate(selectedQuote)}>
                    <FileText className="w-4 h-4 mr-1" />
                    {selectedQuote.estimate ? 'Ouvrir le devis' : 'Rédiger un devis'}
                  </Button>
                </div>
              )}

              {selectedQuote.preferred_date && (
                <div>
                  <p className="text-sm text-slate-500">Date préférée</p>
                  <p className="font-medium">{selectedQuote.preferred_date}</p>
                </div>
              )}

              {canEditQuotes && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Notes internes</p>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Ajouter des notes..."
                    className="min-h-[100px]"
                  />
                  <Button 
                    onClick={handleSaveNotes}
                    className="mt-2 bg-[#00b4d8] hover:bg-[#009bb8]"
                    size="sm"
                  >
                    Enregistrer
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotesPage;
